import { useAppDispatch, useAppSelector } from '../../../../contexts/hooks';
import { newToast, NoDataPanel } from '../../..';
import { shallowEqual } from 'react-redux';
import { formatMessage } from 'devextreme/localization';
import { Button, DataGrid, Toolbar, Tooltip, Popup, FileUploader } from 'devextreme-react';
import { useEffect, useState } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/toolbar';
import { AddIcon, PdfIcon, UnPdfIcon, RemoveIcon, Pin, UnPin, resButtonRefresh } from '../../../../images/icons';
import { ToolbarItem } from 'devextreme-react/popup';
import TagBox from 'devextreme-react/tag-box';
import { useApiStorage } from '../../../../api/api.Storage';
import { useApiOrders } from '../../../../api/api.orders';
import moment from 'moment';
import { useScreenSize } from '../../../../utils/media-query';
import ConfirmationPopup from '../../../utils/popup-confirmation/PopupConfirmation';
import { setPanelPined, setPanelVisible } from '../../../../contexts/slices/orders';

// Convierte el nombre almacenado del blob a un nombre legible.
// Formato nuevo: {ord}-{est}-{uid8}-{nombre}. Formato viejo: {ord}-{est}-{nombre}.
const getDisplayName = (att: any): string => {
  const raw: string = att?.Oad_Nombre || '';
  const parts = raw.split('-');
  if (parts.length <= 2) return raw;
  const isNewFormat = /^[0-9a-fA-F]{8}$/.test(parts[2]);
  const startIdx = isNewFormat ? 3 : 2;
  return parts.slice(startIdx).join('-');
};

const base64ToBlobUrl = (base64: string): string => {
  const sanitized = base64.replace(/\s/g, '');
  const byteCharacters = atob(sanitized);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([byteNumbers], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};

const Index = () => {
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const studies = useAppSelector((x) => x.globalOrders.CurrentOrder.StudiesData);

  const Prm_Ord_Attachments_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Attachments_C, shallowEqual) ?? false;
  const Prm_Ord_Attachments_D = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Attachments_D, shallowEqual) ?? false;
  const currentAttachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const [data, setData] = useState<any>([]);
  const [isPopupVisible, setPopupVisible] = useState(false);
  // Visor de archivos (soporta uno o varios, apilados uno debajo del otro)
  const [showPdfPopup, setShowPdfPopup] = useState(false);
  const [viewerFiles, setViewerFiles] = useState<{ att: any; url: string | null }[]>([]);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerTitle, setViewerTitle] = useState<string>('Vista previa del archivo');
  const [viewerIndex, setViewerIndex] = useState(0);
  // Archivos acumulados antes de subir (permite agregar varios de a uno y quitar alguno).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Valor controlado del FileUploader: se limpia tras cada selección para poder seguir agregando.
  const [uploaderValue, setUploaderValue] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [availableStudies, setAvailableStudies] = useState<any[]>([]);
  const [selectedStudies, setSelectedStudies] = useState<number[]>([]);
  const [validateAsAttachment, setValidateAsAttachment] = useState(false);
  const [modalConfirmation, setModalConfirmation] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<any>(null);
  const { Upload, Delete, GetFile } = useApiStorage();
  const { findOrder } = useApiOrders();
  const { isXSmall, isSmall } = useScreenSize();
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const dispatch = useAppDispatch();
  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };
  const handleRefresh = () => {
    findOrder();
  };

  useEffect(() => {
    if (studies && currentAttachments) {
      const rows = studies.map((study) => {
        const studyAttachments = currentAttachments.filter((attachment) => attachment.Oad_Es_Id === study.Es_Id);
        return {
          Id: study.Es_Id,
          Estudio: study.Es_Nombre,
          Codigo: study.Es_Codigo,
          Archivo: studyAttachments.length > 0,
          Count: studyAttachments.length,
          Attachments: studyAttachments,
          Oad_Id: studyAttachments.length > 0 ? studyAttachments[0].Oad_Id : null,
        };
      });
      setData(rows);
    }
  }, [studies, currentAttachments]);

  useEffect(() => {
    // Todos los estudios están disponibles: un estudio puede recibir varios archivos.
    if (studies) {
      setAvailableStudies(studies);
    }
  }, [studies]);

  const handleFileUpload = (e) => {
    const files: File[] = Array.isArray(e.value) ? e.value : e.value ? [e.value] : [];
    if (files.length === 0) return; // ignoramos el reset del uploader

    const pdfFiles = files.filter((f) => f && f.type === 'application/pdf');
    if (pdfFiles.length !== files.length) {
      newToast('WARNING', 'Solo se permiten archivos PDF. Los archivos no válidos fueron ignorados.', 4);
    }
    if (pdfFiles.length > 0) {
      // Acumulamos, evitando duplicados exactos (mismo nombre + tamaño).
      setPendingFiles((prev) => {
        const merged = [...prev];
        pdfFiles.forEach((nf) => {
          if (!merged.some((ef) => ef.name === nf.name && ef.size === nf.size)) merged.push(nf);
        });
        return merged;
      });
    }
    // Limpiamos el uploader para poder volver a seleccionar (incluso el mismo archivo).
    setUploaderValue([]);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAccept = async () => {
    if (!Prm_Ord_Attachments_C) {
      newToast('WARNING', 'No tiene permisos para crear archivos adjuntos.', 4);
      return;
    }

    if (!pendingFiles || pendingFiles.length === 0) {
      newToast('WARNING', 'Debe adjuntar al menos un archivo antes de continuar.', 4);
      return;
    }

    if (!idOrd || idOrd === -1) {
      newToast('WARNING', 'El ID de la orden es obligatorio.', 4);
      return;
    }

    try {
      setLoading(true);
      // Se sube cada archivo; cada uno se asocia a todos los estudios seleccionados.
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('idOrd', idOrd.toString());
        selectedStudies.forEach((id) => formData.append('idEst', id.toString()));
        formData.append('validate', validateAsAttachment.toString());
        await Upload(formData);
      }
      handleCancel();
    } catch (error) {
      console.error('Error al subir el archivo:', error);
      alert('Hubo un problema al subir el archivo. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPendingFiles([]);
    setUploaderValue([]);
    setSelectedStudies([]);
    setValidateAsAttachment(false);
    setPopupVisible(false);
  };

  const handleStudyChange = (e: any) => {
    const value = (e.value as number[]) || [];
    setSelectedStudies(value);
    if (value.length === 0) setValidateAsAttachment(false);
  };

  const handleDeleteAttachment = async (attachment: any) => {
    if (!Prm_Ord_Attachments_D) {
      newToast('WARNING', 'No tiene permisos para eliminar archivos adjuntos.', 4);
      return;
    }
    if (attachment) {
      setSelectedAttachment(attachment);
      setModalConfirmation(true);
    }
  };

  // Abre el visor con uno o varios archivos (apilados). Descarga cada PDF y arma su blob.
  const openViewer = async (attachments: any[], title?: string) => {
    if (!attachments || attachments.length === 0) return;
    setShowPdfPopup(true);
    setViewerTitle(title || 'Vista previa del archivo');
    setViewerLoading(true);
    setViewerIndex(0);
    setViewerFiles(attachments.map((att) => ({ att, url: null })));
    try {
      const results = await Promise.all(
        attachments.map(async (att) => {
          try {
            const base64 = await GetFile(att.Oad_Id);
            if (base64 && base64.file) return { att, url: base64ToBlobUrl(base64.file) };
          } catch (err) {
            console.error('Error al procesar el archivo PDF:', err);
          }
          return { att, url: null };
        })
      );
      setViewerFiles(results);
    } finally {
      setViewerLoading(false);
    }
  };

  const renderAttachedFiles = () => {
    if (!currentAttachments || !Array.isArray(currentAttachments)) {
      return null;
    }

    return (
      <div className='custom-card-container'>
        {currentAttachments
          .filter((attachment) => attachment.Oad_Es_Id === null)
          .map((attachment) => {
            const displayName = getDisplayName(attachment);
            const truncatedName = displayName.length > 15 ? `${displayName.substring(0, 15)}...` : displayName;

            return (
              <div key={attachment.Oad_Id}>
                <div id={`file${attachment.Oad_Id}`} className='custom-card'>
                  {!isXSmall && !isSmall && Prm_Ord_Attachments_D && <img src={RemoveIcon} alt='Remove Icon' className='delete-icon' onClick={() => handleDeleteAttachment(attachment)} title='Eliminar' />}
                  <div
                    className='custom-card-body'
                    onClick={() => {
                      openViewer([attachment], displayName);
                    }}>
                    <img src={PdfIcon} alt='PDF Icon' className='custom-card-icon' />
                    <p className='file-size'>{(attachment.Oad_Tamanno / 1024).toFixed(2)} KB</p>
                    <div className='custom-card-header' title={displayName} data-tooltip={displayName}>
                      {truncatedName}
                    </div>
                  </div>
                  {(isXSmall || isSmall) && Prm_Ord_Attachments_D && (
                    <button className='delete-button' onClick={() => handleDeleteAttachment(attachment)}>
                      Eliminar
                    </button>
                  )}
                </div>
                <Tooltip target={`#file${attachment.Oad_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'bottom'}>
                  <div className='tooltip-content'>
                    <h6 className='tooltip-title'>Detalles del archivo</h6>
                    <p className='tooltip-item'>
                      <strong>Nombre: </strong>
                      {displayName}
                    </p>
                    <p className='tooltip-item'>
                      <strong>Tamaño: </strong>
                      {(attachment.Oad_Tamanno / 1024).toFixed(2)} KB
                    </p>
                    <p className='tooltip-item'>
                      <strong>Fecha de carga: </strong>
                      {moment(attachment.Oad_Fecha).format('DD/MM/YYYY HH:mm')}
                    </p>
                  </div>
                </Tooltip>
              </div>
            );
          })}
      </div>
    );
  };

  const handleConfirmDelete = () => {
    if (!Prm_Ord_Attachments_D) {
      newToast('WARNING', 'No tiene permisos para eliminar archivos adjuntos.', 4);
      handleCancelDelete();
      return;
    }

    setLoadingDelete(true);
    const deletedId = selectedAttachment?.Oad_Id;
    Delete(deletedId)
      .then(() => {
        // Si el archivo eliminado estaba abierto en el visor, lo quitamos y liberamos su blob.
        setViewerFiles((prev) => {
          const target = prev.find((f) => f.att.Oad_Id === deletedId);
          if (target?.url) URL.revokeObjectURL(target.url);
          const next = prev.filter((f) => f.att.Oad_Id !== deletedId);
          if (next.length === 0) setShowPdfPopup(false);
          else setViewerIndex((i) => Math.min(i, next.length - 1));
          return next;
        });
      })
      .finally(() => {
        handleCancelDelete();
        setLoadingDelete(false);
      });
  };

  const handleCancelDelete = () => {
    setModalConfirmation(false);
  };

  const handleClosePdfPopup = () => {
    setShowPdfPopup(false);
    viewerFiles.forEach((f) => {
      if (f.url) URL.revokeObjectURL(f.url);
    });
    setViewerFiles([]);
  };

  // Visor tipo carrusel: muestra un archivo a la vez con navegación anterior/siguiente.
  const renderViewerBody = () => {
    if (viewerLoading && viewerFiles.every((f) => !f.url)) {
      return <div style={{ textAlign: 'center', marginTop: '20px' }}>Cargando documento...</div>;
    }
    if (viewerFiles.length === 0) {
      return <div style={{ textAlign: 'center', marginTop: '20px' }}>No hay archivos para mostrar.</div>;
    }
    const idx = Math.min(viewerIndex, viewerFiles.length - 1);
    const current = viewerFiles[idx];
    const multiple = viewerFiles.length > 1;
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', background: '#f5f5f5', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
            {multiple && (
              <>
                <Button icon='chevronleft' stylingMode='text' disabled={idx === 0} onClick={() => setViewerIndex(Math.max(0, idx - 1))} />
                <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {idx + 1} / {viewerFiles.length}
                </span>
                <Button icon='chevronright' stylingMode='text' disabled={idx >= viewerFiles.length - 1} onClick={() => setViewerIndex(Math.min(viewerFiles.length - 1, idx + 1))} />
              </>
            )}
            <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getDisplayName(current.att)}>
              {getDisplayName(current.att)}
            </span>
          </div>
          {Prm_Ord_Attachments_D && <Button icon='trash' text='Eliminar' stylingMode='text' onClick={() => handleDeleteAttachment(current.att)} />}
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {current.url ? (
            <iframe src={current.url} title={getDisplayName(current.att)} width='100%' height='100%' style={{ border: 'none', display: 'block' }} />
          ) : (
            <div style={{ textAlign: 'center', padding: 20, color: '#f44336' }}>No se pudo cargar este archivo.</div>
          )}
        </div>
      </div>
    );
  };

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  return (
    <div style={{ height: 'calc(100vh - 300px)', padding: '5px', overflow: 'auto', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
      <Toolbar>
        <Item location='before'>
          <div className='grid-header'>Administración de archivos</div>
        </Item>
        {Prm_Ord_Attachments_C && (
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button
              id='OpportunityBtn'
              icon={AddIcon}
              text='Oportunidad'
              stylingMode='text'
              onClick={() => {
                if (!Prm_Ord_Attachments_C) {
                  newToast('WARNING', 'No tiene permisos para crear archivos adjuntos.', 4);
                  return;
                }
                setPopupVisible(true);
              }}
            />
            <Tooltip target={'#OpportunityBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Nuevo archivo adjunto
            </Tooltip>
          </Item>
        )}
        <Item location='before' locateInMenu='auto'>
          <div className='separator' />
        </Item>
        <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
          <Button id='RefrescarAdjuntos' icon={resButtonRefresh} text='Refresh' stylingMode='text' onClick={handleRefresh} />
          <Tooltip target={'#RefrescarAdjuntos'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            Refrescar datos
          </Tooltip>
        </Item>
        <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
          <Button id='Showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
          <Tooltip target={'#Showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
          </Tooltip>
        </Item>
      </Toolbar>

      {renderAttachedFiles()}

      <DataGrid className='grid' dataSource={data} height='auto' width={'100%'} repaintChangesOnly={false} highlightChanges={false} hoverStateEnabled={true} rowAlternationEnabled={true} showColumnLines={true}>
        <Column dataField='Id' caption='ID' width={50} allowEditing={false} visible={false} />
        <Column dataField='Codigo' caption='CÓDIGO' width={100} allowEditing={false} />
        <Column dataField='Estudio' caption='ESTUDIO' allowEditing={false} />
        <Column
          dataField='Archivo'
          caption='ARCHIVO'
          width={100}
          allowEditing={false}
          cellRender={(e) => (
            <div id={`detailFile${e.data.Id}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
              <img
                src={e.data.Archivo ? PdfIcon : UnPdfIcon}
                alt=''
                style={{ width: '20px', height: '20px', cursor: e.data.Archivo ? 'pointer' : 'default' }}
                onClick={() => {
                  if (e.data.Archivo) {
                    openViewer(e.data.Attachments, e.data.Estudio);
                  }
                }}
              />
              {e.data.Count > 1 && (
                <span
                  style={{ background: '#65ABF2', color: '#fff', borderRadius: '10px', padding: '0 6px', fontSize: '11px', fontWeight: 600, minWidth: 16, textAlign: 'center', lineHeight: '16px' }}
                  title={`${e.data.Count} archivos adjuntos`}>
                  {e.data.Count}
                </span>
              )}
              <Tooltip target={`#detailFile${e.data.Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {e.data.Archivo ? (e.data.Count > 1 ? `${e.data.Count} archivos adjuntos` : 'Ver archivo adjunto') : 'Estudio sin archivo adjunto'}
              </Tooltip>
            </div>
          )}
        />
        {Prm_Ord_Attachments_D && (
          <Column
            dataField='Eliminar'
            caption='ELIMINAR'
            width={100}
            allowEditing={false}
            cellRender={(e) => (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <i
                  onClick={() => {
                    if (!e.data.Archivo) return;
                    if (e.data.Count > 1) {
                      // Varios archivos: se eliminan individualmente desde el visor.
                      openViewer(e.data.Attachments, e.data.Estudio);
                    } else {
                      handleDeleteAttachment(e.data.Attachments[0]);
                    }
                  }}
                  className='dx-icon-trash'
                  title={e.data.Count > 1 ? 'Eliminar (ver archivos)' : 'Eliminar'}
                  aria-label='Eliminar'
                  style={{ cursor: e.data.Archivo ? 'pointer' : 'default', opacity: e.data.Archivo ? 1 : 0.5 }}
                />
              </div>
            )}
          />
        )}
      </DataGrid>

      {isPopupVisible && (
        <Popup visible={isPopupVisible} onHiding={handleCancel} dragEnabled={true} showCloseButton onHidden={handleCancel} showTitle={true} title='Adjuntar archivo' width={400} height={'auto'}>
          <FileUploader selectButtonText='Agregar archivo(s)' labelText='' accept='.pdf' multiple={true} uploadMode='useForm' value={uploaderValue} onValueChanged={handleFileUpload} />
          {pendingFiles.length > 0 && (
            <div style={{ marginTop: 6, marginBottom: 6, maxHeight: 160, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
              {pendingFiles.map((f, i) => (
                <div key={`${f.name}-${f.size}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderBottom: i < pendingFiles.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }} title={f.name}>
                    {f.name}
                  </span>
                  <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>{(f.size / 1024).toFixed(1)} KB</span>
                  <Button icon='close' stylingMode='text' hint='Quitar' onClick={() => removePendingFile(i)} />
                </div>
              ))}
            </div>
          )}
          <TagBox
            dataSource={availableStudies}
            displayExpr='Es_Nombre'
            valueExpr='Es_Id'
            value={selectedStudies}
            label='Seleccione uno o varios estudios (Opcional)'
            labelMode='floating'
            stylingMode='outlined'
            showSelectionControls={true}
            searchEnabled={true}
            onValueChanged={handleStudyChange}
          />
          {selectedStudies.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <label>
                <input type='checkbox' checked={validateAsAttachment} onChange={(e) => setValidateAsAttachment(e.target.checked)} />
                Validar estudio(s) como adjunto
              </label>
            </div>
          )}
          <ToolbarItem toolbar='bottom' location='after'>
            <Button text='Cancelar' onClick={handleCancel} stylingMode='contained' />
            <Button text={loading ? 'Cargando...' : `Cargar`} disabled={loading} onClick={handleAccept} stylingMode='contained' type='default' style={{ marginLeft: '10px' }} />
          </ToolbarItem>
        </Popup>
      )}

      <Popup visible={showPdfPopup} onHiding={handleClosePdfPopup} onHidden={handleClosePdfPopup} showCloseButton showTitle={true} title={viewerTitle} fullScreen dragEnabled>
        {renderViewerBody()}
      </Popup>

      <ConfirmationPopup visible={modalConfirmation} loading={loadingDelete} message='¿Está seguro de que desea eliminar este archivo adjunto?' onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />
    </div>
  );
};

export default Index;
